'use client';

import { useState, useEffect, useRef } from 'react';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from 'components/ui/alert';

import { useCertification } from '../contexts/certification-context';

declare global {
  interface Window {
    onYouTubeIframeAPIReady: () => void;
    YT: {
      Player: new (
        elementId: string,
        options: {
          videoId: string;
          playerVars?: {
            autoplay?: number;
            controls?: number;
            modestbranding?: number;
            loop?: number;
            rel?: number;
            fs?: number;
          };
          events?: {
            onReady?: (event: any) => void;
            onStateChange?: (event: any) => void;
            onError?: (event: any) => void;
          };
        }
      ) => {
        getCurrentTime: () => number;
        getDuration: () => number;
        playVideo: () => void;
        pauseVideo: () => void;
      };
      PlayerState: {
        ENDED: number;
        PLAYING: number;
        PAUSED: number;
        BUFFERING: number;
        CUED: number;
      };
    };
  }
}

export function VideoStep({ step }: { step: number }) {
  const { selectedCertificate, videosCompleted, setVideosCompleted } = useCertification();

  const [isWatching, setIsWatching] = useState(false);
  const [progress, setProgress] = useState(0);
  const [apiReady, setApiReady] = useState(false);
  const [playerReady, setPlayerReady] = useState(false);
  const playerRef = useRef<any>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Use the provided videoId or fallback to default
  const currentVideoId = selectedCertificate?.steps[step].videoId || 'HXV3zeQKqGY';

  useEffect(() => {
    // Initialize YouTube API
    if (!window.YT) {
      // Create YouTube API callback
      window.onYouTubeIframeAPIReady = () => {
        setApiReady(true);
      };
    } else {
      setApiReady(true);
    }

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (apiReady && !playerRef.current) {
      playerRef.current = new window.YT.Player('youtube-player', {
        videoId: currentVideoId,
        playerVars: {
          autoplay: 0,
          controls: 0,
          loop: 1,
          modestbranding: 1,
          rel: 0,
          fs: 1,
        },
        events: {
          onReady: () => {
            setPlayerReady(true);
          },
          onStateChange: (event) => {
            handlePlayerStateChange(event);
          },
          onError: (event) => {
            console.error('YouTube Player Error:', event);
          },
        },
      });
    }
  }, [apiReady, currentVideoId]);

  const handlePlayerStateChange = (event: any) => {
    const playerState = event.data;

    if (playerState === window.YT.PlayerState.PLAYING) {
      setIsWatching(true);

      // Start tracking progress
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }

      progressIntervalRef.current = setInterval(() => {
        if (playerRef.current) {
          const currentTime = playerRef.current.getCurrentTime();
          const duration = playerRef.current.getDuration();
          const percentage = (currentTime / duration) * 100;
          setProgress(percentage);

          // Consider video watched when 95% complete
          if (percentage >= 95 && !videosCompleted.includes(step)) {
            // unique list of completed videos
            setVideosCompleted(Array.from(new Set([...videosCompleted, step])));
            if (progressIntervalRef.current) {
              clearInterval(progressIntervalRef.current);
            }
          }
        }
      }, 1000);
    } else if (playerState === window.YT.PlayerState.PAUSED) {
      setIsWatching(false);
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    } else if (playerState === window.YT.PlayerState.ENDED) {
      setIsWatching(false);
      setProgress(100);
      setVideosCompleted(Array.from(new Set([...videosCompleted, step])));
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    }
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;
    document.head.appendChild(script);
    // ...
  }, []);

  return (
    <div className="space-y-6">
      {/* <Script src="https://www.youtube.com/iframe_api" strategy="lazyOnload" /> */}

      <h2 className="text-2xl font-bold">{selectedCertificate?.steps[step].title}</h2>
      {/* <p className="text-muted-foreground">
        Please watch the entire {selectedCertificate?.title || 'training'} video before proceeding
        to the next step.
      </p> */}

      <div className="grid grid-cols-2 md:grid-cols-2">
        <div className="relative overflow-hidden rounded-lg bg-black">
          <div className="aspect-video">
            <div id="youtube-player" className="w-full"></div>
          </div>

          {/* Progress bar */}
          <div className="absolute bottom-0 left-0 h-1 w-full bg-gray-700">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
        <div>{selectedCertificate?.steps[step]?.content}</div>
        {selectedCertificate?.steps[step]?.videoExplain && (
          <div>{selectedCertificate?.steps[step]?.videoExplain}</div>
        )}
      </div>

      <div className="flex items-center space-x-2">
        <div
          className={`h-2 w-2 rounded-full ${
            isWatching ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
          }`}
        ></div>
        {/* <span className="text-sm text-muted-foreground">
          {isWatching ? 'Currently watching...' : 'Paused'}
        </span> */}
        <div className="ml-auto text-sm text-muted-foreground">
          Afspillet: {Math.round(progress)}%
        </div>
      </div>

      {
        videosCompleted.includes(step) && (
          <Alert className="flex items-center gap-2 bg-green-50 border-green-200">
            <div>
              <CheckCircle2 className="h-4 w-4 text-green-800" />
            </div>
            <div>
              {/* <AlertTitle>Videoen set</AlertTitle> */}
              <AlertDescription className="text-green-800">
                Du kan nu fortsætte til næste trin.
              </AlertDescription>
            </div>
          </Alert>
        )
        // : (
        //   <Alert variant="destructive" className="bg-red-50">
        //     <AlertCircle className="h-4 w-4" />
        //     <AlertDescription>You must watch the entire video before proceeding.</AlertDescription>
        //   </Alert>
        // )
      }
    </div>
  );
}
